import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { money } from "@/lib/format";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/clientes/")({ component: Clientes });

function Clientes() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("customers")
        .select("id,full_name,email,phone,company_name,user_id,created_at,orders(id,total,status,created_at,paid_at)")
        .order("created_at", { ascending: false })
        .limit(500);
      setRows(data ?? []);
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      (r.full_name ?? "").toLowerCase().includes(q) ||
      (r.email ?? "").toLowerCase().includes(q) ||
      (r.phone ?? "").toLowerCase().includes(q) ||
      (r.company_name ?? "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  return (
    <div>
      <h2 className="font-display text-xl font-semibold text-foreground">Clientes</h2>
      <Input
        placeholder="Buscar por nombre, email, teléfono o compañía..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mt-4 max-w-xl bg-surface border-hairline"
      />
      <p className="mt-2 text-[11px] text-muted-foreground">Toca cualquier fila para abrir el perfil del cliente.</p>
      <div className="mt-4 overflow-x-auto rounded-md border border-hairline">
        <table className="w-full text-sm">
          <thead className="bg-surface text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="p-3">Nombre</th>
              <th className="p-3">Contacto</th>
              <th className="p-3">Cuenta</th>
              <th className="p-3"># Pedidos</th>
              <th className="p-3">Total pagado</th>
              <th className="p-3">Último pedido</th>
              <th className="sticky right-0 bg-surface p-3 text-right">Acción</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const orders = r.orders ?? [];
              const paidOrders = orders.filter((o: any) => o.status !== "nuevo" && o.status !== "cancelado");
              const totalPaid = paidOrders.reduce((a: number, o: any) => a + Number(o.total ?? 0), 0);
              const lastOrder = orders.slice().sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
              const goToProfile = () => navigate({ to: "/admin/clientes/$id", params: { id: r.id } });
              return (
                <tr
                  key={r.id}
                  onClick={goToProfile}
                  className="cursor-pointer border-t border-hairline align-top transition-colors hover:bg-surface-2"
                >
                  <td className="p-3 text-foreground">
                    {r.full_name}
                    {r.company_name && <div className="text-[10px] text-muted-foreground">🏢 {r.company_name}</div>}
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">
                    <div>{r.email}</div>
                    <div>{r.phone ?? "—"}</div>
                  </td>
                  <td className="p-3 text-xs">{r.user_id ? "👤 sí" : <span className="text-muted-foreground">no</span>}</td>
                  <td className="p-3 text-foreground">{orders.length}</td>
                  <td className="p-3 text-foreground">{money(totalPaid)}</td>
                  <td className="p-3 text-xs text-muted-foreground">
                    {lastOrder ? <>
                      {new Date(lastOrder.created_at).toLocaleDateString()}
                      <div className="text-[10px]">{lastOrder.status}</div>
                    </> : "—"}
                  </td>
                  <td className="sticky right-0 z-10 bg-surface p-3 text-right">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!r.id) { toast.error("Cliente sin ID"); return; }
                        navigate({ to: "/admin/clientes/$id", params: { id: r.id } });
                      }}
                      className="cursor-pointer inline-flex items-center rounded-md border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/20"
                    >
                      Ver →
                    </button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-sm text-muted-foreground">Sin clientes.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
